
import {Vue, Component, Watch} from "vue-property-decorator";
import notifications from "@/lib/notifications";
import OrderActions from "@/components/order/OrderActions.vue";
import OrderTextCards from "@/components/order/OrderTextCards.vue";
import {getModule} from "vuex-module-decorators";
import {SystemModule, OrderModule, CompanyModule} from "@/store/modules";
import {DefaultTextArea} from "@/models/api/orders/defaults";
import permissions from "@/security/permissions";
import moment from "moment";
import {formatDateWithTime} from "../utils";
import {IOrderTypeToDeliveryTypeMapping, IOrderResource} from "@/store/types";
import _ from "lodash";
import {ORDER_TYPE_RETURN} from "@/consts";
import {OrderTypeToDeliveryTypeMappingModule} from "@/store/modules";
import {FacilityDetailsResource} from "@/models/api/system/facilityDetails";

@Component({
  components: {
    OrderActions,
    OrderTextCards
  }
})
export default class NewOrder extends Vue {
  orderModule: OrderModule = getModule(OrderModule);
  systemModule: SystemModule = getModule(SystemModule);
  companyModule: CompanyModule = getModule(CompanyModule);
  orderTypeToDeliveryTypeMappings: Array<IOrderTypeToDeliveryTypeMapping> = [];

  loading: boolean = false;
  canEditConsignee: boolean = false;
  canEditShipper: boolean = false;
  availableCommodities: any = null;
  availableManufacturers: any = null;
  debounceTime: number = 600;
  textAreas: Array<DefaultTextArea> = null;
  deliveryForId = null;

  async created() {
    this.orderModule.resetCurrentOrder();

    this.deliveryForId = null;
    this.billTo = null;
    this.textAreas = [
      {
        title: "Order Remarks",
        updateName: "Remarks",
        value: this.currentOrder.remarks,
        permission: this.hasPermission(permissions.order.view.remark)
      },
      {
        title: "Driver Notes",
        updateName: "DriverNote",
        value: this.currentOrder.driverNote,
        permission: true
      }
    ];

    const orderTypeToDeliveryTypeMappingModule = getModule(OrderTypeToDeliveryTypeMappingModule);
    this.orderTypeToDeliveryTypeMappings = await orderTypeToDeliveryTypeMappingModule.getOrderTypeToDeliveryTypeMapping();
  }

  mounted() {
    this.orderModule.resetCurrentOrder();
    this.addCommodity();
    this.loading = false;
  }

  unmounted() {
    this.orderModule.resetCurrentOrder();
  }

  get commodities() {
    return this.orderModule.currentOrderCommodities;
  }

  set commodities(newVal) {
    this.orderModule.setOrderCommodities(newVal);
  }

  @Watch("systemModule.agentFacilities")
  agentFacilitiesChanged(newValue) {
    if (this.systemModule.agentFacilities === null || this.currentOrder.facility === null) {
      return;
    }

    const matchingFacility = this.systemModule.agentFacilities.filter(x => x.facilityCode == this.currentOrder.facility);

    if(matchingFacility.length === 0) {
      this.facility = null
    }
  }

  @Watch("deliveryForId", {deep: true})
  deliverForIdChanged(newValue) {
    this.facility = null;
    this.billTo = null;
    this.manufacturer = null;
    this.orderModule.changeCurrentOrderParty({type: "orderby", party: {}});
    this.orderModule.changeCurrentOrderParty({type: "shipper", party: {}});
    this.orderModule.changeCurrentOrderParty({type: "consignee", party: {}});
  }

  @Watch("billTo")
  billToChanged(newValue) {
    this.billToChange(newValue);
  }

  get deliveryTypes() {
    return this.systemModule.deliveryTypes;
  }

  get currentOrder(): IOrderResource {
    return this.orderModule.currentOrder;
  }

  set currentOrder(newVal) {
    this.orderModule.setCurrentOrder(newVal);
  }

  get receivedDate(): string {
    if (this.orderModule.currentOrder) {
      return formatDateWithTime(this.orderModule.currentOrder.anticipatedReceiveDate);
    }
    return "";
  }

  set receivedDate(newValue) {
    newValue;
  }

  get scheduledDeliveryDate() {
    return this.orderModule.currentOrder.scheduledDeliveryDate;
  }

  set scheduledDeliveryDate(newValue) {
    this.orderModule.updateScheduledDeliveryDate(newValue);
  }

  get hasShipsWithOrders(): any {
    if (this.orderModule.currentOrder) {
      return this.orderModule.currentOrderShipsWithOrders;
    }
    return false;
  }

  get billTo(): any {
    return this.orderModule.currentOrder.billTo.id;
  }

  set billTo(value: any) {
    this.orderModule.updateBillTo(value);
  }

  get carrier(): string {
    return this.orderModule.currentOrder.carrier;
  }

  set carrier(value: string) {
    this.orderModule.updateCarrier(value);
  }

  get crn() {
    return this.orderModule.currentOrder.crn;
  }

  set crn(value: string) {
    this.orderModule.updateCrn(value);
  }

  get codPending(): boolean {
    return this.orderModule.currentOrder.codPending;
  }

  set codPending(value: boolean) {
    this.orderModule.updateCodPending(value);
  }

  get customerNotReady(): boolean {
    return this.orderModule.currentOrder.customerNotReady;
  }

  get shipDate() {
    return this.orderModule.currentOrder.shipDate;
  }

  set shipDate(value) {
    this.orderModule.updateShipDate(value);
  }

  get actualReceiveDate() {
    return this.orderModule.currentOrder.actualReceiveDate;
  }

  set actualReceiveDate(value) {
    this.orderModule.updateActualReceivedDate(value);
  }

  get requestedDeliveryDate() {
    return this.orderModule.currentOrder.requestedDeliveryDate;
  }

  set requestedDeliveryDate(value) {
    this.orderModule.updateRequestedDeliveryDate(value);
  }

  get followUpDate() {
    return this.orderModule.currentOrder.followUpDate;
  }

  set followUpDate(value) {
    this.orderModule.updateFollowUpDate(value);
  }

  get dockSearchDate() {
    return this.orderModule.currentOrder.dockSearchDate;
  }

  set dockSearchDate(value) {
    this.orderModule.updateDockSearchDate(value);
  }

  get facility() {
    return this.orderModule.currentOrder.facility;
  }

  set facility(value) {
    this.orderModule.updateFacility(value);
  }

  get manufacturer() {
    return this.orderModule.currentOrder.manufacturer;
  }

  set manufacturer(value) {
    this.orderModule.updateManufacturer(value);
  }

  get rateBy() {
    return this.orderModule.currentOrder.rateBy;
  }

  set rateBy(value) {
    this.orderModule.updateRateBy(value);
  }

  get orderType() {
    return this.orderModule.currentOrder.orderType;
  }

  set orderType(value) {
    this.orderModule.updateOrderType(value);
  }

  get status() {
    return this.orderModule.currentOrder.status;
  }

  set status(value) {
    this.orderModule.updateOrderStatus(value);
  }

  get deliveryType() {
    return this.orderModule.currentOrder.deliveryType;
  }

  set deliveryType(value) {
    this.orderModule.updateDeliveryType(value);

    const map = this.orderTypeToDeliveryTypeMappings.find(x => x.deliveryType === value);
    const orderType = map ? map.orderType : "";
    this.orderModule.updateOrderType(orderType);
  }

  get timeframeStart() {
    return moment(this.orderModule.currentOrder.timeframeStart).format("HH:mm");
  }

  get timeframeEnd() {
    return moment(this.orderModule.currentOrder.timeframeEnd).format("HH:mm");
  }

  /* reb
  get facilities() {
    return this.systemModule.facilities;
  }
  */

  // sf - this is a dynamic solution to handle this "the vuex way" https://vuex.vuejs.org/guide/forms.html
  updateOrder(prop, e) {
    const method = "update" + prop;
    this.orderModule[method](e);
  }

  updateCommodity(i, type, e) {
    if (e !== null || e !== undefined) {
      let commodities = _.cloneDeep(this.commodities);
      commodities[i][type] = e;
      this.orderModule.setOrderCommodities(commodities);
    }
  }

  addCommodity() {
    this.orderModule.addOrderCommodity(this.createNewCommodity());
  }

  removeCommodity(index) {
    this.orderModule.deleteOrderCommodity(index);
  }

  createNewCommodity() {
    return { commodityCode: null, quantity: null, numberOfCubes: null, weight: null };
  }

  async filterCommodities(companyId) {
    const commodityResults = await this.systemModule.searchCommoditiesByCode({
      billTo: companyId,
      code: null,
      deliveryForId: this.deliveryForId
    });
    const results = commodityResults.map(x => ({
      label: x.agentId,
      value: x.code
    }));
    this.availableCommodities = results;
  }

  async filterManufacturers(companyId) {
    const manufacturerResults = await this.systemModule.searchManufacturersByName({
      billTo: companyId,
      name: "",
      deliveryForId: this.deliveryForId
    });
    const results = manufacturerResults.map(x => ({
      label: x.name,
      value: x.id
    }));

    this.availableManufacturers = results;

    if (this.availableManufacturers && this.availableManufacturers.length == 1) {
      this.orderModule.updateManufacturer(this.availableManufacturers[0].value);
    } else if (
      !this.availableManufacturers ||
      this.availableManufacturers.filter(x => x.value == this.currentOrder.manufacturer).length == 0
    ) {
      this.orderModule.updateManufacturer(null);
    }
  }

  async billToChange(companyId) {
    this.filterCommodities(companyId);
    this.filterManufacturers(companyId);

    await this.updateOrderBy(companyId);
  }

  async updateOrderBy(companyId) {
    if (companyId && companyId != "") {
      try {
        const companyResponse = await this.companyModule.getCompanyById(
          {
            companyId: companyId,
            deliveryForId: this.deliveryForId ?? 0
          }
        );

        if (companyResponse.data) {
          this.orderModule.changeCurrentOrderParty({
            type: "orderby",
            party: companyResponse.data
          });
        }
      } catch (ex) {
        console.log(`ERROR: {ex}`)
      }
    }
  }

  async updateAppropriateParty() {
    let currentFacility = this.systemModule.agentFacilities.find(x => x.facilityCode === this.currentOrder.facility);
    if (currentFacility == null)
      return;

    let shipperId = this.deliveryForId && this.deliveryForId > 0 ?
      currentFacility.agentShipperCompanyId : currentFacility.shipperCompanyId;

    if (currentFacility) {
      const companyResponse = await this.companyModule.getCompanyById(
        {
          companyId: shipperId,
          deliveryForId: this.deliveryForId ?? 0
        }
      );
      let targetParty = "shipper";
      let otherTarget = "consignee";
      this.canEditConsignee = true;
      this.canEditShipper = true;

      if (this.currentOrder.orderType === ORDER_TYPE_RETURN) {
        targetParty = "consignee";
        otherTarget = "shipper";
      }

      this.orderModule.changeCurrentOrderParty({type: targetParty, party: companyResponse.data});

      if (
        this.currentOrder[otherTarget] &&
        this.currentOrder[otherTarget].id === shipperId
      )
        this.orderModule.changeCurrentOrderParty({type: otherTarget, party: {}});
    }
  }

  partyUpdated(party, type) {
    this.orderModule.changeCurrentOrderParty({party, type});
  }

  async createOrder() {
    this.loading = true;
    this.currentOrder.deliveryForId = this.deliveryForId;
    await this.orderModule
      .createOrder()
      .then((res: any) => {
        notifications.success(
          "Order Created",
          `Order #${res.data.headerNumber} successfully created.`
        );
        this.$router.push(`/order/${res.data.headerNumber}`);
      })
      .catch(ex => {
        this.loading = false;
        notifications.error("Order not created", "Order not created", ex);
      });
  }
}
